// src/pages/Leaderboard.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Button from "../central/Button";
import Panel from "../central/Panel";
import { HeadingMedium } from "../central/Typography";
import { getDoc, getDocs, doc, query, where } from "firebase/firestore";
import { paths } from "../lib/paths";
import { col, ref } from "../lib/fw";
import { isUnlimitedFT } from "../utils/freeTransfers";
import PageLoader from "../components/PageLoader";
import { useSeason } from "../context/SeasonContext";
import SeasonSelector from "../components/SeasonSelector";

const TOTAL_SENTINEL = "TOTAL";

export default function Leaderboard() {
  const { leagueID } = useParams();
  const { selectedSeasonID } = useSeason();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);             // [{id, name, displayName}]
  const [teamDisplayNames, setTeamDisplayNames] = useState({}); // { [teamID]: displayName }
  const [gameweeks, setGameweeks] = useState([]);     // ["GW1", "GW2", ...]
  const [selectedView, setSelectedView] = useState(TOTAL_SENTINEL); // "TOTAL" | "GWx"
  const [rows, setRows] = useState([]);               // computed leaderboard rows
  const [loading, setLoading] = useState(true);

  // ---- helpers --------------------------------------------------------------

  const computePlayerFinal = (pid, captainID, chipUsed, playerPointsMap) => {
    const rec = playerPointsMap[pid] || { total: 0, breakdown: {} };
    const base = Number(rec.total) || 0;
    const b = rec.breakdown || {};

    // chip extras (add before captain multiplier)
    let chipExtra = 0;
    if (chipUsed === "Goal Boost") {
      chipExtra += (Number(b.goals) || 0) + (Number(b.assists) || 0);
    } else if (chipUsed === "Fortress") {
      chipExtra += (Number(b.cleanSheet) || 0) + (Number(b.cleanSheetGK) || 0);
    }

    const isCaptain = pid === captainID;
    const mult = isCaptain ? (chipUsed === "Triple Captain" ? 3 : 2) : 1;

    return (base + chipExtra) * mult;
  };

  const computeTeamPointsForGW = (teamDoc, gwID, playerPointsMap) => {
    if (!teamDoc) return 0;

    const selections =
      teamDoc.selections?.[gwID] || { DEF: [], MID: [], FWD: []};
    const picks = ["DEF", "MID", "FWD"].flatMap((k) => selections[k] || []);

    const captainID =
      teamDoc.captains?.[gwID] ??
      teamDoc.captain ??
      null;

    const chipUsed =
      teamDoc.chipsUsed?.[gwID] ??
      teamDoc.chip ??
      teamDoc.chips?.[gwID] ??
      "None";

    return picks.reduce(
      (sum, pid) =>
        sum + computePlayerFinal(pid, captainID, chipUsed, playerPointsMap),
      0
    );
  };

  // --- transfer penalty helpers (mirror YourPoints.js logic) ------------------
  const normalizemaxFreeTransfers = (teamLeagueDoc, gwID, leagueDefaults) => {
    const tf = teamLeagueDoc?.transfersFree?.[gwID];
    if (isUnlimitedFT(tf)) return Infinity;
    if (tf !== undefined && tf !== null) {
      const n = Number(tf);
      if (!Number.isNaN(n)) return n;
    }
    return Number(leagueDefaults?.maxmaxFreeTransfers) ?? 0;
  };

  const transfersUsedForGW = (teamLeagueDoc, gwID) => {
    const raw = teamLeagueDoc?.transfers?.[gwID];
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const computeTransferPenalty = (teamLeagueDoc, gwID, leagueDefaults) => {
    const used = transfersUsedForGW(teamLeagueDoc, gwID);
    const free = normalizemaxFreeTransfers(teamLeagueDoc, gwID, leagueDefaults);
    if (!Number.isFinite(free)) return 0; // unlimited week => no penalty
    const per = Number(leagueDefaults?.transferPenaltyPoints) || 0;
    const over = Math.max(0, used - free);
    return -over * per; // negative value (deduction)
  };

  // ---- load static lists ----------------------------------------------------

  // Fetch teams and user display names in parallel — independent of each other.
  // Previously teams depended on teamDisplayNames, causing a double load.
  useEffect(() => {
    if (!leagueID) return;
    let cancelled = false;
    (async () => {
      try {
        const [teamsSnap, usersSnap] = await Promise.all([
          getDocs(paths.leagueTeams(leagueID, selectedSeasonID)),
          getDocs(query(col("users"), where("leagueIDs", "array-contains", leagueID))),
        ]);
        if (cancelled) return;

        // Build displayName map from users
        const nameMap = {};
        usersSnap.forEach((d) => {
          const u = d.data() || {};
          const teamIdForLeague = u.teams?.[leagueID];
          if (teamIdForLeague) {
            nameMap[String(teamIdForLeague)] = u.displayName || u.name || "";
          }
        });
        setTeamDisplayNames(nameMap);

        // Build teams list, joining display names immediately
        const list = teamsSnap.docs.map((d) => {
          const data = d.data() || {};
          const teamId = String(d.id);
          return {
            id: teamId,
            name: data.name || "Unnamed Team",
            displayName: nameMap[teamId] || "",
          };
        });
        setTeams(list);
      } catch (e) {
        if (cancelled) return;
        console.warn("Leaderboard: failed to load teams/users", e);
        setTeamDisplayNames({});
        setTeams([]);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID, selectedSeasonID]);

  // gameweeks (exclude meta and current gameweek)
  useEffect(() => {
    if (!leagueID) return;
    let cancelled = false;
    (async () => {
      const snap = await getDocs(paths.gameweeksCol(leagueID, selectedSeasonID));
      if (cancelled) return;
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const sorted = rows
        .filter((r) => r.id !== "meta")
        .sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
      const currentGW = sorted[sorted.length - 1]?.id;
      const list = sorted
        .filter((r) => r.id !== currentGW)
        .map((r) => r.id);
      setGameweeks(list);

      // default to TOTAL
      setSelectedView(TOTAL_SENTINEL);
    })();
    return () => { cancelled = true; };
  }, [leagueID, selectedSeasonID]);

  // ---- compute leaderboard for selected view --------------------------------

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!leagueID || teams.length === 0) return;
      setLoading(true);

      // Fetch league defaults once
      const leagueSnap = await getDoc(paths.league(leagueID));
      const leagueDefaults = leagueSnap.exists()
        ? {
            maxmaxFreeTransfers: leagueSnap.data()?.maxmaxFreeTransfers ?? 1,
            transferPenaltyPoints: leagueSnap.data()?.transferPenaltyPoints ?? 5,
          }
        : { maxmaxFreeTransfers: 1, transferPenaltyPoints: 5 };

      // Helper to load points map once per GW
      const loadPointsForGW = async (gwID) => {
        const ptsSnap = await getDocs(paths.gwPlayerPoints(leagueID, gwID, selectedSeasonID));
        const map = {};
        ptsSnap.forEach((d) => {
          const data = d.data() || {};
          map[d.id] = {
            total: Number(data.total) || 0,
            breakdown: data.breakdown || {},
          };
        });
        return map;
      };

      // Helper to load the best team doc for a team/gw (snapshot → live)
      const loadTeamDocForGW = async (teamId, gwID) => {
        let tSnap = await getDoc(
          doc(paths.gwTeams(leagueID, gwID, selectedSeasonID), String(teamId))
        );
        if (!tSnap.exists()) {
          tSnap = await getDoc(
            doc(paths.leagueTeams(leagueID, selectedSeasonID), String(teamId))
          );
        }
        return tSnap.exists() ? { id: tSnap.id, ...tSnap.data() } : null;
      };

      const loadLeagueTeamDoc = async (teamId) => {
        const t = await getDoc(
          doc(paths.leagueTeams(leagueID, selectedSeasonID), String(teamId))
        );
        return t.exists() ? { id: t.id, ...t.data() } : null;
      };

      let results = [];

      if (selectedView === TOTAL_SENTINEL) {
        // TOTAL: fetch all GW points maps and all team docs in parallel
        const [gwPointsMaps, leagueTeamDocs] = await Promise.all([
          // All GW points maps at once
          Promise.all(gameweeks.map((gw) => loadPointsForGW(gw))).then(
            (maps) => Object.fromEntries(gameweeks.map((gw, i) => [gw, maps[i]]))
          ),
          // All league-level team docs at once
          Promise.all(teams.map((t) => loadLeagueTeamDoc(t.id))),
        ]);

        // For each team, fetch all its GW snapshot docs in parallel
        results = await Promise.all(
          teams.map(async (t, ti) => {
            const leagueTeamDoc = leagueTeamDocs[ti];
            const gwDocs = await Promise.all(
              gameweeks.map((gw) => loadTeamDocForGW(t.id, gw))
            );
            const total = gameweeks.reduce((sum, gw, gi) => {
              const penalty = computeTransferPenalty(leagueTeamDoc, gw, leagueDefaults);
              return sum + computeTeamPointsForGW(gwDocs[gi], gw, gwPointsMaps[gw]) + penalty;
            }, 0);
            return {
              id: t.id,
              team: t.name,
              displayName: t.displayName,
              points: Math.round(total),
            };
          })
        );
      } else {
        // Single GW: fetch points map + all team docs in parallel
        const [pointsMap, gwDocs, leagueTeamDocs] = await Promise.all([
          loadPointsForGW(selectedView),
          Promise.all(teams.map((t) => loadTeamDocForGW(t.id, selectedView))),
          Promise.all(teams.map((t) => loadLeagueTeamDoc(t.id))),
        ]);

        results = teams.map((t, i) => {
          const pts = computeTeamPointsForGW(gwDocs[i], selectedView, pointsMap);
          const penalty = computeTransferPenalty(leagueTeamDocs[i], selectedView, leagueDefaults);
          return {
            id: t.id,
            team: t.name,
            displayName: t.displayName,
            points: Math.round(pts + penalty),
          };
        });
      }

      // sort by points desc (rank recalculated per view)
      if (cancelled) return;
      results.sort((a, b) => b.points - a.points);
      setRows(results);
      setLoading(false);
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueID, teams, gameweeks, selectedView, selectedSeasonID]);

  // ---- UI -------------------------------------------------------------------

  return (
    <>
      <div className="p-4 flex flex-col items-center">
        <SeasonSelector />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem", marginBottom: "1.5rem", width: "100%" }}>
          {/* Total toggle */}
          <button
            onClick={() => setSelectedView(TOTAL_SENTINEL)}
            style={{
              background: selectedView === TOTAL_SENTINEL ? "#50B4C8" : "#f4f7f8",
              color: selectedView === TOTAL_SENTINEL ? "#fff" : "#222",
              border: "1px solid #e5e7eb",
              borderRadius: "999px",
              padding: "0.3rem 0.85rem",
              fontSize: "0.85rem",
              fontWeight: "600",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Total
          </button>
          {/* GW arrow pill */}
          {(() => {
            const gwList = gameweeks;
            const currentIndex = gwList.indexOf(selectedView);
            const isOnGW = currentIndex !== -1;
            const canPrev = isOnGW ? currentIndex > 0 : gwList.length > 0;
            const canNext = isOnGW && currentIndex < gwList.length - 1;
            const arrowBtn = (enabled) => ({
              background: "none", border: "none", cursor: enabled ? "pointer" : "default",
              padding: "0.3rem 0.6rem", fontSize: "1rem",
              color: enabled ? "#50B4C8" : "#ccc", fontWeight: "700", lineHeight: 1,
            });
            const label = isOnGW ? selectedView : "GW";
            return (
              <div style={{ display: "inline-flex", alignItems: "center", background: "#f4f7f8", borderRadius: "999px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
                <button style={arrowBtn(canPrev)} disabled={!canPrev}
                  onClick={() => {
                    if (!canPrev) return;
                    setSelectedView(isOnGW ? gwList[currentIndex - 1] : gwList[gwList.length - 1]);
                  }}
                  aria-label="Previous gameweek">←</button>
                <span style={{ fontWeight: 600, fontSize: "0.85rem", minWidth: 44, textAlign: "center", color: "#222", padding: "0 0.1rem" }}>{label}</span>
                <button style={arrowBtn(canNext)} disabled={!canNext}
                  onClick={() => canNext && setSelectedView(gwList[currentIndex + 1])}
                  aria-label="Next gameweek">→</button>
              </div>
            );
          })()}
        </div>

        {loading ? (
          <PageLoader message="Loading leaderboard…" />
        ) : rows.length === 0 ? (
          <p>No teams found.</p>
        ) : (
          <Panel style={{ width: "100%", maxWidth: "48rem" }}>
            <div style={{ overflowX: "auto" }}>
            <table className="table-auto w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-2 py-0.5" style={{ textAlign: "center" }}>Rank</th>
                  <th className="px-2 py-0.5" style={{ textAlign: "left" }}>Team</th>
                  <th className="px-2 py-0.5" style={{ textAlign: "center" }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const podium = [
                    { bg: "rgba(255, 215, 0, 0.13)", color: "#a07800" },
                    { bg: "rgba(192, 192, 192, 0.2)", color: "#5a5a5a" },
                    { bg: "rgba(205, 127, 50, 0.13)", color: "#7a4a1a" },
                  ][idx] || { bg: "transparent", color: "inherit" };
                  return (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/yourpoints/${leagueID}/${r.id}`)}
                      style={{ background: podium.bg, cursor: "pointer" }}
                    >
                      <td className="px-2 py-0.5" style={{ textAlign: "center", fontWeight: idx < 3 ? 700 : 400, color: podium.color }}>
                        {idx + 1}
                      </td>
                      <td className="px-2 py-0.5" style={{ textAlign: "left" }}>
                        <div style={{ lineHeight: "1.15", fontWeight: idx < 3 ? 700 : 400, color: podium.color }}>{r.team}</div>
                        {r.displayName && (
                          <div style={{ fontSize: "0.75rem", color: "#777", lineHeight: "1.1", marginTop: "1px" }}>
                            {r.displayName}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-0.5" style={{ textAlign: "center", fontWeight: idx < 3 ? 700 : 400, color: podium.color }}>
                        {r.points}
                      </td>
                      <td className="px-2 py-0.5" style={{ textAlign: "right", fontWeight: 700, width: "1.5rem", color: idx < 3 ? podium.color : "#9ca3af" }}>
                        ›
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Panel>
        )}
      </div>
    </>
  );
}